import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { isFleetOnly, FLEET_HOME } from "../config/productConfig";
import { ArrowLeft, FileText } from "lucide-react";
import { DocumentosValidades } from "../components/student/PilotBlocks";

// Os documentos carregados para mim (pela escola ou por mim): ver e descarregar uma cópia.
const dataPt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const ESTADO = {
  pendente: { txt: "aguarda aprovação", cls: "text-sky-300" },
  aprovado: { txt: "aprovado", cls: "text-emerald-300" },
  recusado: { txt: "recusado", cls: "text-red-300" },
  carregado: { txt: "carregado", cls: "text-slate-300" },
};

export default function MeusDocumentosPage() {
  const navigate = useNavigate();
  const { user, school } = useAuth();
  // "Voltar" goes to the reservations page on fleet-only, and to the training dashboard otherwise.
  // O administrador (que também é piloto ou instrutor) volta ao seu painel
  const voltar = () => navigate(["admin", "super_admin"].includes(user?.role) ? "/admin" : isFleetOnly(school) ? FLEET_HOME : "/dashboard");
  const [anexos, setAnexos] = useState([]);
  const [semFicha, setSemFicha] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/di/documents/me/anexos`, { withCredentials: true });
      setAnexos(data || []);
      setSemFicha(false);
    } catch (e) {
      setSemFicha(e?.response?.status === 403);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (semFicha) {
    return (
      <div className="min-h-screen bg-cockpit-bg p-4">
        <Button variant="ghost" onClick={voltar}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
        <p className="text-slate-300 mt-6 max-w-md">A tua conta não está ligada a uma ficha de piloto ou de instrutor desta escola. Pede ao administrador para a ligar.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg p-4 space-y-4" data-testid="meus-documentos-page">
      <Button variant="ghost" onClick={voltar}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>

      <DocumentosValidades />

      <Card className="instrument-panel max-w-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-200">
            <FileText className="w-5 h-5" /> Os meus documentos ({anexos.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          {anexos.length === 0 && <p className="text-slate-400">Ainda não há documentos carregados para ti.</p>}
          {anexos.map((a) => (
            <div key={a.id} className="border-t border-slate-800 pt-2 flex flex-wrap items-center justify-between gap-2" data-testid="meu-anexo">
              <span className="text-slate-200">
                {a.label} · validade {dataPt(a.valid_until)}
                <span className="block text-[10px] text-slate-500 truncate max-w-xs" title={a.filename}>{a.filename}</span>
              </span>
              <span className="flex items-center gap-3">
                <span className={ESTADO[a.estado]?.cls || "text-slate-400"}>{ESTADO[a.estado]?.txt || a.estado}</span>
                <a href={`${API}/di/documents/me/anexos/${a.id}/download`} target="_blank" rel="noreferrer" className="text-sky-300 underline" data-testid="meu-anexo-ver">Ver</a>
                <a href={`${API}/di/documents/me/anexos/${a.id}/download`} download={a.filename} className="text-sky-300 underline" data-testid="meu-anexo-descarregar">Descarregar</a>
              </span>
              {a.estado === "recusado" && a.nota_recusa && <span className="w-full text-red-200">Motivo: {a.nota_recusa}</span>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
